import React from 'react';
import { useQuery } from 'react-query';
import {
  FiPackage,
  FiDollarSign,
  FiTrendingUp,
  FiAlertTriangle,
} from 'react-icons/fi';
import { reportService } from '../services/apiService';
import StatCard from '../components/StatCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatCurrency, formatDateTime } from '../utils/formatters';

const Dashboard = () => {
  const { data, isLoading, error } = useQuery(
    'dashboard',
    reportService.getDashboard,
    { refetchInterval: 30000 }
  );

  if (isLoading) return <LoadingSpinner message="Loading dashboard..." />;

  if (error) {
    return (
      <div className="empty-state">
        <FiAlertTriangle size={48} />
        <h3>Failed to load dashboard</h3>
        <p>{error.message}</p>
      </div>
    );
  }

  const dashboardData = data?.data || {};
  const summary = dashboardData.summary || {};
  const lowStock = dashboardData.low_stock || [];
  const recentSales = dashboardData.recent_sales || [];

  const stockValueUsd = summary.stock_value_usd ?? summary.stock_value ?? 0;
  const stockValueLrd = summary.stock_value_lrd ?? 0;

  const todaySales = summary.today_sales || 0;
  const todayTxns = summary.today_transactions || 0;
  const weekSales = summary.week_sales || 0;
  const weekTxns = summary.week_transactions || 0;
  const monthSales = summary.month_sales || 0;
  const monthTxns = summary.month_transactions || 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            Overview of your business activity
          </p>
        </div>
      </div>

      {/* Stock & Inventory Summary */}
      <div className="stats-grid">
        <StatCard
          icon={<FiPackage size={24} />}
          value={summary.total_products || 0}
          label="Total Products"
          color="blue"
        />
        <StatCard
          icon={<FiDollarSign size={24} />}
          value={
            <div>
              <div>{formatCurrency(stockValueUsd)}</div>
              {stockValueLrd > 0 && (
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    fontWeight: 500,
                  }}
                >
                  L${stockValueLrd.toFixed(2)}
                </div>
              )}
            </div>
          }
          label="Stock Value"
          color="green"
        />
        <StatCard
          icon={<FiAlertTriangle size={24} />}
          value={lowStock.length}
          label="Low Stock Items"
          color="orange"
        />
      </div>

      {/* Sales Summary — Today / Week / Month */}
      <div
        style={{
          marginTop: '8px',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Sales Overview
      </div>
      <div className="stats-grid">
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={
            <div>
              <div>{formatCurrency(todaySales)}</div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  fontWeight: 500,
                  marginTop: 2,
                }}
              >
                {todayTxns} {todayTxns === 1 ? 'transaction' : 'transactions'}
              </div>
            </div>
          }
          label="Today"
          color="purple"
        />
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={
            <div>
              <div>{formatCurrency(weekSales)}</div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  fontWeight: 500,
                  marginTop: 2,
                }}
              >
                {weekTxns} {weekTxns === 1 ? 'transaction' : 'transactions'}
              </div>
            </div>
          }
          label="This Week"
          color="blue"
        />
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={
            <div>
              <div>{formatCurrency(monthSales)}</div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  fontWeight: 500,
                  marginTop: 2,
                }}
              >
                {monthTxns} {monthTxns === 1 ? 'transaction' : 'transactions'}
              </div>
            </div>
          }
          label="This Month"
          color="green"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {/* Low Stock Alert */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Low Stock Alert</h2>
          </div>
          {lowStock.length === 0 ? (
            <div className="empty-state">
              <FiPackage size={48} />
              <h3>All products have sufficient stock</h3>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Current</th>
                    <th>Reorder Level</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.map((item) => (
                    <tr key={item.product_id}>
                      <td>{item.product_name}</td>
                      <td>
                        <span className="badge badge-danger">
                          {item.current_stock}
                        </span>
                      </td>
                      <td>{item.reorder_level}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Sales */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Recent Sales</h2>
          </div>
          {recentSales.length === 0 ? (
            <div className="empty-state">
              <FiTrendingUp size={48} />
              <h3>No recent sales</h3>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSales.map((sale) => (
                    <tr key={sale.sale_id}>
                      <td>{sale.customer_name || 'Walk-in'}</td>
                      <td>
                        <strong>{formatCurrency(sale.total_amount)}</strong>
                      </td>
                      <td>{formatDateTime(sale.sale_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
